import { useState, useCallback, useMemo } from "react";
import { debounce } from "lodash";
import {
  Device,
  InsertTarget,
  InsertToMode,
  PendingKlydo,
} from "../../../Types";
import { Klydo } from "../../../api/klydo";
import useClocksInfiniteQuery from "../../Device/hooks/useClocksInfiniteQuery";
import firebaseService from "../../../firebase_service/firebaseService";
import { isSealedKlydoType } from "../../../utils/validatePendingKlydos";
import usePlayKlydos from "./usePlayKlydos";

export { usePlayKlydos };

type UseSendKlydoModalProps = {
  klydos: Klydo[];
  onlyDev?: boolean;
};

const useSendKlydoModal = ({ klydos, onlyDev }: UseSendKlydoModalProps) => {
  const [selectedDevices, setSelectedDevices] = useState<Array<Device>>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [textSearch, setTextSearch] = useState<string>("");
  const [advancedSearchResults, setAdvancedSearchResults] = useState<Device[]>(
    [],
  );

  const [insertModes, setInsertModes] = useState<Set<InsertToMode>>(
    () => new Set<InsertToMode>(["FEED"]),
  );
  const [exploreCategoryTarget, setExploreCategoryTarget] =
    useState<string>("");
  const [ttlEnabled, setTtlEnabled] = useState(false);
  const [ttl, setTtl] = useState<number>(60);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledTime, setScheduledTime] = useState<string>("");
  const [playNow, setPlayNow] = useState(false);
  const [forceDownload, setForceDownload] = useState(false);

  const handleFreeSearch = useCallback(
    debounce((s: string) => setTextSearch(s), 500),
    [],
  );

  const {
    data: devices,
    fetchNextPage,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({ query: textSearch, onlyDevClocks: !!onlyDev });

  const toggleInsertMode = useCallback((mode: InsertToMode) => {
    setInsertModes((prev) => {
      const next = new Set(prev);
      if (next.has(mode)) {
        next.delete(mode);
      } else {
        next.add(mode);
      }
      return next;
    });
  }, []);

  const buildInsertTargets = useCallback((): InsertTarget[] => {
    const modes: InsertToMode[] = ["FEED", "FAVORITE", "EXPLORE"];
    return modes
      .filter((mode) => insertModes.has(mode))
      .map((mode) => ({
        mode,
        target: mode === "EXPLORE" ? exploreCategoryTarget : null,
      }));
  }, [insertModes, exploreCategoryTarget]);

  // Direct Firestore write, as before the API detour: read the machine doc,
  // merge by id (an existing entry is REPLACED so re-sending updates its
  // settings), write the whole field back.
  const addPendingKlydosToDevice = async (
    device: Device,
    newEntries: PendingKlydo[],
    draftKlydoIds: string[],
  ) => {
    const matchingDevices = await firebaseService.getDevicesByIdfs([
      device.idf,
    ]);
    const matchingDevice = matchingDevices.find(
      (item) => item.idf === device.idf,
    );

    if (draftKlydoIds.length) {
      // A missing machines doc is NOT a "regular clock" — surface the real
      // problem instead of a misleading draft rejection.
      if (!matchingDevice) {
        throw new Error(
          "machine doc not found — cannot verify this clock accepts drafts",
        );
      }
      const draftsAllowed =
        matchingDevice.hideDev?.value === false ||
        matchingDevice.isArtist?.value === true ||
        matchingDevice.isKlydosRater?.value === true;
      if (!draftsAllowed) {
        throw new Error(
          `regular clock — draft klydos (${draftKlydoIds.join(", ")}) can ` +
            "only be sent to a dev/artist/rater clock; deselect them to " +
            "send the rest",
        );
      }
    }

    const existing: PendingKlydo[] = matchingDevice?.pendingKlydos?.value ?? [];

    const newIds = new Set(newEntries.map((entry) => entry.id));
    const merged = [
      ...existing.filter((entry) => !newIds.has(entry.id)),
      ...newEntries,
    ];

    await firebaseService.updateConfig(device.id, "pendingKlydos", merged);
  };

  const handleSendKlydo = async () => {
    if (selectedDevices.length === 0) return false;
    if (!klydos || klydos.length === 0) return false;
    if (insertModes.size === 0) {
      setErrorMessage("Select at least one Insert To target.");
      return false;
    }
    if (insertModes.has("EXPLORE") && !exploreCategoryTarget) {
      setErrorMessage("Pick an Explore category.");
      return false;
    }
    // Clearing the number field yields Number("") === 0 — don't send a
    // 0-minute TTL (the input's min=1 only blocks the spinner).
    if (ttlEnabled && (!ttl || ttl < 1)) {
      setErrorMessage("TTL must be at least 1 minute.");
      return false;
    }
    // insertAt 0 means "insert now" on the clock — with the schedule switch on
    // and no time picked, the send would be immediate, the opposite of intent.
    if (scheduleEnabled && !scheduledTime) {
      setErrorMessage("Pick a scheduled time, or turn scheduling off.");
      return false;
    }

    setErrorMessage(null);
    setIsSending(true);

    const insertTo = buildInsertTargets();
    const insertAt =
      scheduleEnabled && scheduledTime ? new Date(scheduledTime).getTime() : 0;
    const ttlValue = ttlEnabled ? ttl : null;

    const newEntries: PendingKlydo[] = klydos.map((klydo) => ({
      id: klydo.id,
      insertTo,
      ttl: ttlValue,
      insertAt,
      playNow,
      attempts: 0,
      forceDownload,
    }));

    // Sending a draft is only meaningful on a dev/artist/rater clock — a
    // regular clock's fetch never serves drafts, so a queued draft just sits
    // and retries. The device rows here are BigQuery rows WITHOUT those
    // flags, so the check runs on the machines doc read below (deliberately
    // UI-side; there is no server check on this write).
    const draftKlydoIds = klydos
      .filter((klydo) => !isSealedKlydoType(klydo.type))
      .map((klydo) => klydo.id);

    const results = await Promise.allSettled(
      selectedDevices.map((device) =>
        addPendingKlydosToDevice(device, newEntries, draftKlydoIds),
      ),
    );

    const failures = results
      .map((result, idx) => ({ result, device: selectedDevices[idx] }))
      .filter(({ result }) => result.status === "rejected");

    setIsSending(false);

    if (failures.length > 0) {
      const summary = failures
        .map(({ device, result }) => {
          const reason =
            (result as PromiseRejectedResult).reason instanceof Error
              ? ((result as PromiseRejectedResult).reason as Error).message
              : String((result as PromiseRejectedResult).reason);
          return `${device.idf || device.id}: ${reason}`;
        })
        .join("; ");
      setErrorMessage(
        `Failed for ${failures.length}/${selectedDevices.length} device(s): ${summary}`,
      );
      return false;
    }

    return true;
  };

  const toggleDeviceSelection = useCallback((device: Device) => {
    setSelectedDevices((prev) =>
      prev.some((d) => d.id === device.id)
        ? prev.filter((d) => d.id !== device.id)
        : [...prev, device],
    );
  }, []);

  const handleSelectAll = useCallback((data: Device[]) => {
    setSelectedDevices((prev) => (prev.length === data.length ? [] : data));
  }, []);

  const handleSendAndClose = async (onClose: () => void) => {
    const success = await handleSendKlydo();
    if (success) {
      onClose();
    }
    return success;
  };

  const currentData = useMemo(
    () =>
      advancedSearchResults.length && !textSearch.length
        ? advancedSearchResults
        : devices || [],
    [advancedSearchResults, textSearch, devices],
  );

  const resetModalState = useCallback(() => {
    setSelectedDevices([]);
    setErrorMessage(null);
    setIsSending(false);
    setTextSearch("");
    setAdvancedSearchResults([]);
    setInsertModes(new Set<InsertToMode>(["FEED"]));
    setExploreCategoryTarget("");
    setTtlEnabled(false);
    setTtl(60);
    setScheduleEnabled(false);
    setScheduledTime("");
    setPlayNow(false);
    setForceDownload(false);
  }, []);

  return {
    resetModalState,
    selectedDevices,
    errorMessage,
    isSending,
    textSearch,
    handleFreeSearch,
    currentData,
    fetchNextPage,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    handleSendKlydo,
    handleSendAndClose,
    toggleDeviceSelection,
    handleSelectAll,
    advancedSearchResults,
    setAdvancedSearchResults,
    insertModes,
    toggleInsertMode,
    exploreCategoryTarget,
    setExploreCategoryTarget,
    ttlEnabled,
    setTtlEnabled,
    ttl,
    setTtl,
    scheduleEnabled,
    setScheduleEnabled,
    scheduledTime,
    setScheduledTime,
    playNow,
    setPlayNow,
    forceDownload,
    setForceDownload,
  };
};

export default useSendKlydoModal;
